import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Info, Search } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DownloadReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  deduplication: 'deduplicated' | 'raw';
}
type ReportFormat = 'itemized' | 'summary';
type ColumnsView = 'default' | 'all' | 'custom';
const ITEMIZED_COLUMNS = [{
  id: 'charge_id',
  label: 'Charge ID',
  group: 'left'
}, {
  id: 'payment_intent_id',
  label: 'Payment Intent ID',
  group: 'left'
}, {
  id: 'invoice_id',
  label: 'Invoice ID',
  group: 'left'
}, {
  id: 'created',
  label: 'Created',
  group: 'left'
}, {
  id: 'currency',
  label: 'Currency',
  group: 'left'
}, {
  id: 'presentment_amount',
  label: 'Presentment amount',
  group: 'left'
}, {
  id: 'card_brand',
  label: 'Card brand',
  group: 'left'
}, {
  id: 'card_type',
  label: 'Card type',
  group: 'left'
}, {
  id: 'card_input_method',
  label: 'Card input method',
  group: 'left'
}, {
  id: 'card_country',
  label: 'Card country',
  group: 'left'
}, {
  id: 'outcome_type',
  label: 'Outcome type',
  group: 'left'
}, {
  id: 'blocked_reason',
  label: 'Blocked reason',
  group: 'right'
}, {
  id: 'decline_reason',
  label: 'Decline reason',
  group: 'right'
}, {
  id: 'attributable_optimization',
  label: 'Attributable optimization',
  group: 'right'
}, {
  id: 'final_attempt',
  label: 'Final attempt',
  group: 'right'
}, {
  id: 'final_charge_id',
  label: 'Final charge ID',
  group: 'right'
}, {
  id: 'used_network_tokens',
  label: 'Used network tokens',
  group: 'right'
}, {
  id: 'interaction_type',
  label: 'Interaction type',
  group: 'right'
}, {
  id: 'retry_status',
  label: 'Retry status',
  group: 'right'
}, {
  id: 'avs_response',
  label: 'AVS Response',
  group: 'right'
}, {
  id: 'cvc_response',
  label: 'CVC Response',
  group: 'right'
}, {
  id: '3ds_outcome',
  label: '3DS outcome',
  group: 'right'
}];
const SUMMARY_COLUMNS = [{
  id: 'start_time',
  label: 'Start time for period'
}, {
  id: 'report_currency',
  label: 'Report currency'
}, {
  id: 'pivot_column',
  label: 'Pivot column selected'
}, {
  id: 'total_authorized_count',
  label: 'Total authorized count'
}, {
  id: 'total_payment_count',
  label: 'Total payment count'
}, {
  id: 'total_authorized_amount',
  label: 'Total authorized amount'
}, {
  id: 'total_payment_amount',
  label: 'Total payment amount'
}];
export function DownloadReportModal({
  isOpen,
  onClose,
  dateRange,
  deduplication
}: DownloadReportModalProps) {
  const {
    t
  } = useTranslation('analytics');
  const [reportFormat, setReportFormat] = useState<ReportFormat>('itemized');
  const [columnsView, setColumnsView] = useState<ColumnsView>('default');
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [emailNotification, setEmailNotification] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const dateRangeText = `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`;
  const getInfoMessage = () => {
    if (deduplication === 'raw') {
      return 'This report shows raw data, which includes all attempted card payments.';
    }
    return 'This report shows only final attempt payments. To see all payments, switch to the raw view.';
  };
  const currentColumns = reportFormat === 'itemized' ? ITEMIZED_COLUMNS : SUMMARY_COLUMNS;
  const filteredColumns = currentColumns.filter(col => col.label.toLowerCase().includes(searchQuery.toLowerCase()));
  const toggleColumn = (columnId: string) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(columnId)) {
      newSelected.delete(columnId);
    } else {
      newSelected.add(columnId);
    }
    setSelectedColumns(newSelected);
  };
  const selectAllColumns = () => {
    setSelectedColumns(new Set(currentColumns.map(col => col.id)));
  };
  const deselectAllColumns = () => {
    setSelectedColumns(new Set());
  };
  const handleShowColumns = () => {
    if (columnsView === 'default') {
      setColumnsView('custom');
      // Pre-select all columns when showing
      selectAllColumns();
    } else {
      setColumnsView('default');
    }
  };
  const getColumnCount = () => {
    if (columnsView === 'default') {
      return reportFormat === 'itemized' ? 22 : 7;
    }
    return selectedColumns.size;
  };
  const handleDownload = () => {
    // Implement download logic here
    console.log('Downloading report...', {
      dateRange: dateRangeText,
      deduplication,
      reportFormat,
      columnsView,
      selectedColumns: Array.from(selectedColumns),
      emailNotification
    });
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose} data-id="element-1788">
      <div role="dialog" aria-modal="true" aria-labelledby="download-report-modal-title" className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1789">
        <div className="p-6" data-id="element-1790">
          {/* Header */}
          <div className="flex items-center justify-between mb-6" data-id="element-1791">
            <h2 id="download-report-modal-title" className="text-xl font-semibold text-gray-900" data-id="element-1792">
              {t('components.DownloadReportModal.download_report')}{' '}
              {dateRangeText}
            </h2>
            <button onClick={onClose} aria-label={t('components.AddChartModal.cancel')} className="text-gray-400 hover:text-gray-600" data-id="element-1793">
              <X size={20} aria-hidden="true" data-id="element-1794" />
            </button>
          </div>

          {/* Info Banner */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex gap-3" data-id="element-1795">
            <Info size={20} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1796" />
            <p className="text-sm text-gray-700" data-id="element-1797">{getInfoMessage()}</p>
          </div>

          {/* Report Format */}
          <div className="mb-6" data-id="element-1798">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1799">
              {t('components.DownloadReportModal.report_format')}
            </h3>
            <div className="flex gap-4" data-id="element-1800">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-1801">
                <input type="radio" name="format" checked={reportFormat === 'itemized'} onChange={() => {
                setReportFormat('itemized');
                setColumnsView('default');
                setSelectedColumns(new Set());
              }} className="w-4 h-4 text-blue-600" data-id="element-1802" />

                <span className="text-sm text-gray-900" data-id="element-1803">
                  {t('components.DownloadReportModal.itemized')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-1804">
                <input type="radio" name="format" checked={reportFormat === 'summary'} onChange={() => {
                setReportFormat('summary');
                setColumnsView('default');
                setSelectedColumns(new Set());
              }} className="w-4 h-4 text-blue-600" data-id="element-1805" />

                <span className="text-sm text-gray-900" data-id="element-1806">
                  {t('components.DownloadReportModal.summary')}
                </span>
              </label>
            </div>
          </div>

          {/* Columns */}
          <div className="mb-6" data-id="element-1807">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1808">
              {t('components.DownloadReportModal.columns')}
            </h3>
            <div className="flex items-center gap-2 mb-3" data-id="element-1809">
              <select className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" value={`all-${getColumnCount()}`} disabled data-id="element-1810">
                <option data-id="element-1811">
                  {t('components.DownloadReportModal.all')}
                  {getColumnCount()})
                </option>
              </select>
              <button onClick={handleShowColumns} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2" data-id="element-1812">
                {columnsView === 'default' ? <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-1813">
                      <path d="M8 3.5V12.5M8 12.5L11.5 9M8 12.5L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1814" />
                    </svg>
                    {t('components.DownloadReportModal.show')}
                  </> : <>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-1815">
                      <path d="M8 12.5V3.5M8 3.5L4.5 7M8 3.5L11.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1816" />
                    </svg>
                    {t('components.DownloadReportModal.hide')}
                  </>}
              </button>
            </div>

            {/* Column Selection */}
            {columnsView === 'custom' && <div className="border border-gray-200 rounded-lg" data-id="element-1817">
                {/* Search */}
                <div className="p-3 border-b border-gray-200" data-id="element-1818">
                  <div className="relative" data-id="element-1819">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1820" />

                    <input type="text" placeholder={t('components.DownloadReportModal.attrs.filter_by_name_or_metadata_key')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-1821" />
                  </div>
                </div>

                {/* Column List */}
                <div className="max-h-80 overflow-y-auto p-3" data-id="element-1822">
                  {reportFormat === 'itemized' ? <div className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-1823">
                      {/* Left Column */}
                      <div data-id="element-1824">
                        {filteredColumns.filter(col => col.group === 'left').map(column => <label key={column.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2" data-id="element-1825">
                              <input type="checkbox" checked={selectedColumns.has(column.id)} onChange={() => toggleColumn(column.id)} className="w-4 h-4 text-blue-600 rounded" data-id="element-1826" />

                              <span className="text-sm text-gray-900" data-id="element-1827">
                                {column.label}
                              </span>
                            </label>)}
                      </div>

                      {/* Right Column */}
                      <div data-id="element-1828">
                        {filteredColumns.filter(col => col.group === 'right').map(column => <label key={column.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2" data-id="element-1829">
                              <input type="checkbox" checked={selectedColumns.has(column.id)} onChange={() => toggleColumn(column.id)} className="w-4 h-4 text-blue-600 rounded" data-id="element-1830" />

                              <span className="text-sm text-gray-900" data-id="element-1831">
                                {column.label}
                              </span>
                            </label>)}
                      </div>
                    </div> : <div className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-1832">
                      {filteredColumns.map(column => <label key={column.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2" data-id="element-1833">
                          <input type="checkbox" checked={selectedColumns.has(column.id)} onChange={() => toggleColumn(column.id)} className="w-4 h-4 text-blue-600 rounded" data-id="element-1834" />

                          <span className="text-sm text-gray-900" data-id="element-1835">
                            {column.label}
                          </span>
                        </label>)}
                    </div>}
                </div>
              </div>}
          </div>

          {/* Notifications */}
          <div className="mb-6" data-id="element-1836">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1837">
              {t('components.DownloadReportModal.notifications')}
            </h3>
            <label className="flex items-start gap-2 cursor-pointer" data-id="element-1838">
              <input type="checkbox" checked={emailNotification} onChange={e => setEmailNotification(e.target.checked)} className="w-4 h-4 text-blue-600 rounded mt-0.5" data-id="element-1839" />

              <span className="text-sm text-gray-700" data-id="element-1840">
                {t('components.DownloadReportModal.email_simonverraestceylonteacom_when_your_report_is')}
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-1841">
            <p className="text-sm text-gray-600" data-id="element-1842">
              {t('components.DownloadReportModal.the_file_will_be_saved_as')}
            </p>
            <div className="flex items-center gap-3" data-id="element-1843">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg" data-id="element-1844">
                {t('components.AddChartModal.cancel')}
              </button>
              <button onClick={handleDownload} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-1845">
                {t('components.CardPaymentsBreakdown.download')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}